import { CompanyGuid, LocalTimeString, PriceTierConfig } from '@breezy/shared'
import React, { useMemo } from 'react'
import { useQuery } from 'urql'
import { FETCH_PRICEBOOK_SETTINGS_CONFIG_QUERY } from '../gql/CompanyConfig.gql'
import { useExpectedPrincipal } from '../providers/PrincipalUser'
import { useStrictContext } from '../utils/react-utils'

type PricingSettingsContextType = PriceTierConfig & {
  hasTieredPricingEnabled: boolean
  hasAnyMemberTiersEnabled: boolean
  hasAnyAddonTiersEnabled: boolean
  hasAnyAfterhoursTiersEnabled: boolean
} & {
  pricebookPhotosEnabled: boolean
  fetching: boolean
  refetch: () => void
}

const PricebookSettingsContext = React.createContext<PricingSettingsContextType | undefined>(undefined)

export const PricebookPhotosEnabledProvider = React.memo<React.PropsWithChildren>(({ children }) => {
  const principal = useExpectedPrincipal()
  if (principal.company) {
    return (
      <UnauthPricebookSettingsProvider companyGuid={principal.company.companyGuid}>
        {children}
      </UnauthPricebookSettingsProvider>
    )
  }

  return <>{children}</>
})

PricebookPhotosEnabledProvider.displayName = 'PricebookPhotosEnabledProvider'

type UnauthPricebookSettingsProviderProps = React.PropsWithChildren<{
  companyGuid: CompanyGuid
}>

export const UnauthPricebookSettingsProvider = React.memo<UnauthPricebookSettingsProviderProps>(
  ({ companyGuid, children }) => {
    const [{ data, fetching }, refetch] = useQuery({
      query: FETCH_PRICEBOOK_SETTINGS_CONFIG_QUERY,
      variables: { companyGuid },
      requestPolicy: 'network-only',
    })

    const pricebookPhotosCompanyConfigEnabled = useMemo(() => {
      return data?.companyConfigByPk?.pricebookPhotosEnabled ?? false
    }, [data])

    const pricingTierConfig = useMemo(() => {
      return data?.companyPricingTierConfigByPk
        ? data.companyPricingTierConfigByPk
        : {
            memberTierEnabled: false,
            memberAddonTierEnabled: false,
            memberAfterhoursTierEnabled: false,
            memberAddonAfterhoursTierEnabled: false,
            addonTierEnabled: false,
            addonAfterhoursTierEnabled: false,
            afterhoursTierEnabled: false,
            businessHoursStartTime: '08:00:00' as LocalTimeString,
            businessHoursEndTime: '16:00:00' as LocalTimeString,
            saturdayIsBusinessDay: false,
            sundayIsBusinessDay: false,
          }
    }, [data])

    const pricingTierRollup = useMemo(() => {
      return {
        hasTieredPricingEnabled:
          pricingTierConfig.memberTierEnabled ||
          pricingTierConfig.memberAddonTierEnabled ||
          pricingTierConfig.memberAfterhoursTierEnabled ||
          pricingTierConfig.memberAddonAfterhoursTierEnabled ||
          pricingTierConfig.addonTierEnabled ||
          pricingTierConfig.addonAfterhoursTierEnabled ||
          pricingTierConfig.afterhoursTierEnabled,
        hasAnyMemberTiersEnabled:
          pricingTierConfig.memberAddonTierEnabled ||
          pricingTierConfig.memberAddonAfterhoursTierEnabled ||
          pricingTierConfig.addonTierEnabled ||
          pricingTierConfig.addonAfterhoursTierEnabled,
        hasAnyAddonTiersEnabled:
          pricingTierConfig.memberAddonTierEnabled ||
          pricingTierConfig.memberAddonAfterhoursTierEnabled ||
          pricingTierConfig.addonTierEnabled ||
          pricingTierConfig.addonAfterhoursTierEnabled,
        hasAnyAfterhoursTiersEnabled:
          pricingTierConfig.memberAddonAfterhoursTierEnabled ||
          pricingTierConfig.addonAfterhoursTierEnabled ||
          pricingTierConfig.afterhoursTierEnabled ||
          pricingTierConfig.memberAfterhoursTierEnabled,
      }
    }, [pricingTierConfig])

    return (
      <PricebookSettingsContext.Provider
        value={{
          pricebookPhotosEnabled: pricebookPhotosCompanyConfigEnabled,
          ...pricingTierConfig,
          ...pricingTierRollup,
          fetching,
          refetch,
        }}
      >
        {children}
      </PricebookSettingsContext.Provider>
    )
  },
)

UnauthPricebookSettingsProvider.displayName = 'UnauthPricebookSettingsProvider'

export const usePricebookSettings = () => {
  return useStrictContext(PricebookSettingsContext)
}
